import './index.less';
import { useEffect, useRef, useState } from 'react';
import { ReactComponent as BigAddSvg } from '../icons/add.svg';
import { ReactComponent as AddSvg } from '@/global/icons/side-tool/add.svg';
import { ReactComponent as SelectSvg } from '@/global/icons/side-tool/select.svg';
import { ReactComponent as DeleteSvg } from '@/global/icons/side-tool/delete.svg';
import { ReactComponent as EditSvg } from '@/global/icons/side-tool/edit.svg';
import TnMessage from '@/global/components/message';
import { Button, Form, Input, InputNumber, Modal, Select, Skeleton, Spin } from 'antd';
import classNames from 'classnames';
import MonixModal from '@/global/components/monix-modal';
import TnModal from '@/global/components/base-modal';
import { Position } from '@/global/interface/project-manage/position';
import { createPosition, deletePosition, getPositionDetail, getPositions, updatePosition } from '@/api/lib/position';
import LibPositionAuthDetail from './detail';


export default function LibPositionPanel() {
    const [positions, setPositions] = useState<Position[]>([]) // 职位列表
    const [loading, setLoading] = useState<boolean>(false) // loading
    const [activeItem, setActiveItem] = useState<Position>(null) // 激活的项

    const addBtnRef = useRef(null); // 添加表单的ref
    const editBtnRef = useRef(null); // 编辑表单的ref
    const [addForm] = Form.useForm(); // 添加表单
    const [editForm] = Form.useForm(); // 编辑表单

    const [showAddModel, setShowAddModel] = useState<boolean>(false) // 添加弹窗标志
    const [addLoading, setAddLoading] = useState<boolean>(false) // 添加弹窗loading
    const [showEditModel, setShowEditModel] = useState<boolean>(false) // 编辑弹窗标志
    const [editLoading, setEditLoading] = useState<boolean>(false) // 编辑弹窗loading
    const [needRender, setNeedRender] = useState<boolean>(false) // 重新渲染

    // 初始化
    useEffect(() => {
        generateData(true);
    }, [])

    useEffect(() => {
        if (!showAddModel) {
            addForm.resetFields();
        }
    }, [showAddModel])

    useEffect(() => {
        if (!showEditModel) {
            editForm.resetFields();
        }
    }, [showEditModel])


    // 获取数据
    const generateData = (setActive = false) => {
        setLoading(true);
        getPositions().then(res => {
            if (res.status === 200) {
                let data: Position[] = res.data || [];
                setPositions(data);
                if (setActive && data?.length > 0) {
                    setActiveItem(data[0]);
                }
            }
        }).catch(err => {
            TnMessage.error("获取职位失败");
        }).finally(() => {
            setLoading(false);
        })
    }

    // 点击工具栏添加按钮
    const onAddClick = () => {
        setShowAddModel(true);
    }

    // 点击工具栏编辑按钮
    const onEditClick = () => {
        if (!activeItem) {
            TnMessage.warning("未选中职位");
            return;
        }
        // 将信息注入
        setShowEditModel(true);
        setEditLoading(true);
        getPositionDetail(+activeItem?.id).then(res => {
            let data = res.data;
            editForm.setFieldsValue({ ...data });
        }).catch(err => {
            TnMessage.error("获取职位失败");
        }).finally(() => {
            setEditLoading(false);
        })
    }

    // 点击工具栏删除按钮
    const onDeleteClick = () => {
        if (!activeItem) {
            TnMessage.warning("未选中职位");
            return;
        }
        TnModal().deleteModal({
            onOk() {
                Modal.destroyAll();
                setLoading(true);
                deletePosition(activeItem?.id).then(res => {
                    if (res.status === 200) {
                        let idx = positions?.findIndex(item => item?.id === activeItem?.id);
                        positions.splice(idx, 1);
                        setPositions([...positions]);
                        TnMessage.success("删除成功");
                        setShowEditModel(false);
                    }
                }).catch(err => {
                    TnMessage.error("删除失败");
                }).finally(() => {
                    setLoading(false);
                })
            },
            onCancel() {
                Modal.destroyAll();
            },
            closable: true,
        })
    }

    // 点击添加弹窗确认按钮
    const onAddSaveClick = () => {
        if (addLoading) return;
        if (addBtnRef.current) {
            addBtnRef.current.click();
        }
    }

    // 创建表单提交
    const onAddFinish = (values) => {
        setAddLoading(true);
        let data = { ...values };
        createPosition(data).then(res => {
            if (res.status === 200) {
                let newItem: Position = {
                    id: res.data,
                    ...data,
                }
                positions.push(newItem);
                setPositions([...positions]);
                setActiveItem(newItem);
                TnMessage.success("添加成功");
                setShowAddModel(false);
            }
        }).catch(err => {
            TnMessage.error(err.data || '添加失败');
        }).finally(() => {
            setAddLoading(false);
        });
    }

    // 点击编辑弹窗确认按钮
    const onEditSaveClick = () => {
        if (editLoading) return;
        if (editBtnRef.current) {
            editBtnRef.current.click();
        }
    }

    // 编辑表单提交
    const onEditFinish = (values) => {
        setEditLoading(true);
        let data = { ...values };
        updatePosition(activeItem?.id, data).then(res => {
            if (res.status === 200) {
                let idx = positions?.findIndex(item => item?.id === activeItem?.id);
                positions[idx] = {
                    ...positions[idx],
                    ...data,
                }
                setPositions([...positions]);
                setNeedRender(pre => !pre);
                TnMessage.success("编辑成功");
                setShowEditModel(false);
            }
        }).catch(err => {
            TnMessage.error(err?.data || "编辑失败");
        }).finally(() => {
            setEditLoading(false);
        });
    }

    return (
        <div className='lib-position-container'>
            <div className="ope-header">
                <div className="create-position-btn" onClick={onAddClick}><BigAddSvg />添加职位</div>
                {/* <div className='ope-icon'><SelectSvg /></div> */}
                <div className='ope-icon' onClick={onEditClick}><EditSvg /></div>
                <div className='ope-icon' onClick={onDeleteClick}><DeleteSvg /></div>
            </div>
            <div className="lib-position-panel">
                <Skeleton loading={loading}>
                    <div className="lib-position-list">
                        <div className="lib-header">
                            <div className='header-cell'>职位</div>
                        </div>
                        <div className="lib-body">
                            {positions?.map((item: Position, key) => (
                                <div className={classNames({
                                    "body-row": true,
                                    "body-row-active": activeItem?.id === item?.id,
                                })} key={key} onClick={() => setActiveItem(item)}>
                                    <div className='body-cell'>{item?.name || ''}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Skeleton>
                <div className="lib-position-detail">
                    <div className='lib-header'>设置权限</div>
                    <LibPositionAuthDetail positionId={activeItem?.id} />
                </div>
            </div >

            <MonixModal
                title={"创建职位"}
                visible={showAddModel}
                setVisible={(e) => setShowAddModel(e)}
                onSaveClick={onAddSaveClick}
                wrapClassName="form-modal"
                saveDisable={addLoading}
            >
                <Spin wrapperClassName="form-body" spinning={addLoading}>
                    <Form
                        colon={false}
                        form={addForm}
                        autoComplete="off"
                        labelCol={{ span: 6 }}
                        onFinish={onAddFinish}
                    >
                        <Form.Item label="名称:" name="name" rules={[{ required: true, message: '名称不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Button ref={addBtnRef} htmlType="submit" style={{ display: 'none' }}></Button>
                    </Form>
                </Spin>
            </MonixModal>

            <MonixModal
                title={"编辑职位"}
                visible={showEditModel}
                setVisible={(e) => setShowEditModel(e)}
                onSaveClick={onEditSaveClick}
                wrapClassName="form-modal"
                saveDisable={editLoading}
            >
                <Spin wrapperClassName="form-body" spinning={editLoading}>
                    <Form
                        colon={false}
                        form={editForm}
                        autoComplete="off"
                        labelCol={{ span: 6 }}
                        onFinish={onEditFinish}
                    >
                        <Form.Item label="名称:" name="name" rules={[{ required: true, message: '名称不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Button ref={editBtnRef} htmlType="submit" style={{ display: 'none' }}></Button>
                    </Form>
                </Spin>
            </MonixModal>
        </div >
    )
}